import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, VariantSelectedEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelectedOptimistic, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Listen for slideshow select events to update thumbnail active states
    // Use requestAnimationFrame to ensure slideshow is fully initialized
    requestAnimationFrame(() => {
      if (this.refs.slideshow) {
        this.refs.slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
        // Set initial active state
        const currentIndex = this.refs.slideshow.current ?? 0;
        this.#updateThumbnailActiveState(currentIndex);
      }
    });

    // Check if there's a variant in the URL and reorder on initial load
    this.#handleInitialVariantFromUrl();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Filters gallery media and selects the variant featured image when a swatch is selected on PDP.
   */
  #handleVariantSelectedOptimistic = async (event) => {
    if (!(event instanceof VariantSelectedEvent)) return;
    const variantId = event.detail?.resource?.variantId;
    if (!variantId) return;

    const picker = event.target?.closest?.('variant-picker') ?? event.target;
    if (!picker?.closest || picker.closest('product-card') || picker.closest('quick-add-dialog')) return;

    const productUrl = picker.dataset?.productUrl;
    if (!productUrl || !/\/products\//.test(productUrl)) return;

    await this.#filterMediaByVariantId(this, String(variantId).trim());
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;
    const variantId =
      event.detail?.resource?.id?.toString?.().trim() ||
      source
        ?.querySelector?.('form[action*="/cart/add"] input[name="id"], product-form input[name="id"]')
        ?.value?.trim() ||
      this.#getSelectedVariantIdFromDom();

    if (!source) {
      if (variantId) {
        await this.#filterMediaByVariantId(this, variantId);
      }
      return;
    }

    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    if (variantId && newMediaGallery instanceof HTMLElement) {
      await this.#filterMediaByVariantId(newMediaGallery, variantId);
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles initial variant from URL on page load.
   */
  #handleInitialVariantFromUrl() {
    // Wait for DOM to be ready and variant picker to be initialized
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        requestAnimationFrame(() => this.#applyInitialVariantReordering());
      });
    } else {
      // Use requestAnimationFrame to ensure variant picker is initialized
      requestAnimationFrame(() => {
        setTimeout(() => this.#applyInitialVariantReordering(), 100);
      });
    }
  }

  /**
   * Applies reordering based on the selected variant from URL or initial selection.
   */
  async #applyInitialVariantReordering() {
    const variantId = this.#getSelectedVariantIdFromDom();
    if (!variantId) return;

    await this.#filterMediaByVariantId(this, variantId);
  }

  /**
   * @returns {string | undefined}
   */
  #getSelectedVariantIdFromDom() {
    const section = this.closest('.shopify-section');
    const idInput =
      section?.querySelector?.('form[action*="/cart/add"] input[name="id"]') ??
      section?.querySelector?.('product-form input[name="id"]') ??
      document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (idInput instanceof HTMLInputElement && idInput.value) return idInput.value.trim();
    return undefined;
  }

  /**
   * All variant IDs that share the selected variant's color (or just the variant when no Color option).
   * @param {object} product - Product JSON from /products/handle.js
   * @param {string} variantId
   * @returns {string[]}
   */
  #getColorVariantIds(product, variantId) {
    const needle = String(variantId).trim();
    const variant = product?.variants?.find((v) => String(v.id) === needle);
    if (!variant) return [needle];

    const optionNames = product.options || [];
    const colorIndex = optionNames.findIndex((name) => /^colou?r$/i.test(String(name).trim()));
    if (colorIndex < 0) return [needle];

    const colorKey = `option${colorIndex + 1}`;
    const colorValue = variant[colorKey] ?? variant.options?.[colorIndex];
    if (!colorValue) return [needle];

    return product.variants
      .filter((v) => (v[colorKey] ?? v.options?.[colorIndex]) === colorValue)
      .map((v) => String(v.id));
  }

  /**
   * @param {string} idsAttr
   * @param {string | string[]} variantIds
   */
  #variantIdsAttributeMatches(idsAttr, variantIds) {
    const needles = (Array.isArray(variantIds) ? variantIds : [variantIds]).map((id) => String(id).trim()).filter(Boolean);
    if (!needles.length || !idsAttr) return false;
    const mediaIds = idsAttr.split(',').map((id) => id.trim());
    return mediaIds.some((id) => needles.includes(id));
  }

  /**
   * Media IDs for the selected color: position range in product.media plus featured images.
   * @param {object} product
   * @param {string} variantId
   * @returns {Set<string>}
   */
  #getMediaIdsForSelectedColor(product, variantId) {
    const mediaIds = new Set();
    const variantIdsToMatch = this.#getColorVariantIds(product, variantId);
    const variantIdSet = new Set(variantIdsToMatch);

    for (const variant of product.variants || []) {
      if (!variantIdSet.has(String(variant.id))) continue;
      const featuredId = variant.featured_media?.id ?? variant.featured_image?.id;
      if (featuredId) mediaIds.add(String(featuredId));
    }

    const optionNames = product.options || [];
    const colorIndex = optionNames.findIndex((name) => /^colou?r$/i.test(String(name).trim()));
    const mediaList = product.media || [];

    if (colorIndex < 0 || !mediaList.length) {
      return mediaIds;
    }

    const colorKey = `option${colorIndex + 1}`;
    const selectedVariant = product.variants.find((v) => String(v.id) === String(variantId));
    const selectedColor = selectedVariant?.[colorKey] ?? selectedVariant?.options?.[colorIndex];
    if (!selectedColor) return mediaIds;

    const colorAnchors = new Map();
    for (const variant of product.variants) {
      const color = variant[colorKey] ?? variant.options?.[colorIndex];
      if (!color || colorAnchors.has(color)) continue;
      const featuredId = variant.featured_media?.id ?? variant.featured_image?.id;
      if (!featuredId) continue;
      const idx = mediaList.findIndex((m) => String(m.id) === String(featuredId));
      if (idx >= 0) colorAnchors.set(color, idx);
    }

    const sortedAnchors = [...colorAnchors.entries()].sort((a, b) => a[1] - b[1]);
    const anchorIndex = sortedAnchors.findIndex(([color]) => color === selectedColor);
    if (anchorIndex < 0) return mediaIds;

    const startIdx = sortedAnchors[anchorIndex][1];
    const endIdx =
      anchorIndex < sortedAnchors.length - 1 ? sortedAnchors[anchorIndex + 1][1] : mediaList.length;

    for (let i = startIdx; i < endIdx; i++) {
      mediaIds.add(String(mediaList[i].id));
    }

    const attachedMediaIds = new Set();
    for (const variant of product.variants) {
      const featuredId = variant.featured_media?.id ?? variant.featured_image?.id;
      if (featuredId) attachedMediaIds.add(String(featuredId));
    }

    for (const item of mediaList) {
      const id = String(item.id);
      if (!attachedMediaIds.has(id)) {
        mediaIds.add(id);
      }
    }

    return mediaIds;
  }

  /**
   * @param {object} product
   * @param {string} variantId
   * @returns {string | undefined}
   */
  #getFeaturedMediaId(product, variantId) {
    const variant = product?.variants?.find((v) => String(v.id) === String(variantId));
    const featuredId = variant?.featured_media?.id ?? variant?.featured_image?.id;
    return featuredId != null ? String(featuredId) : undefined;
  }

  /**
   * @param {HTMLElement} slide
   * @param {string} featuredMediaId
   */
  #slideMatchesFeaturedMedia(slide, featuredMediaId) {
    if (!(slide instanceof HTMLElement) || !featuredMediaId) return false;
    return (
      slide.getAttribute('slide-id') === featuredMediaId ||
      this.#getMediaIdFromElement(slide) === featuredMediaId
    );
  }

  /**
   * @param {HTMLElement} element
   * @returns {string | undefined}
   */
  #getMediaIdFromElement(element) {
    if (!(element instanceof HTMLElement)) return undefined;
    const direct = element.getAttribute('data-media-id')?.trim();
    if (direct) return direct;
    const slideId = element.getAttribute('slide-id')?.trim();
    if (slideId) return slideId;
    const nested = element.querySelector('[data-media-id]');
    return nested instanceof HTMLElement ? nested.getAttribute('data-media-id')?.trim() : undefined;
  }

  /**
   * Immediately filters and selects the first slide for this variant (before product JSON loads).
   *
   * @param {HTMLElement} mediaGallery
   * @param {string} variantId
   */
  #prefilterMediaByVariantIdSync(mediaGallery, variantId) {
    const thumbnailsWrapper = mediaGallery.querySelector('.slideshow-controls__thumbnails');
    const slidesContainer = mediaGallery.querySelector('slideshow-slides');
    if (!thumbnailsWrapper || !slidesContainer) return;

    const thumbnails = Array.from(thumbnailsWrapper.querySelectorAll('.slideshow-controls__thumbnail'));
    const slides = Array.from(slidesContainer.querySelectorAll('slideshow-slide'));
    const count = Math.min(thumbnails.length, slides.length);
    const matchingIndices = new Set();

    for (let index = 0; index < count; index++) {
      const ids = this.#getVariantIdsForIndex(slides, thumbnails, index);
      if (ids && this.#variantIdsAttributeMatches(ids, variantId)) {
        matchingIndices.add(index);
      }
    }

    if (matchingIndices.size === 0) return;

    thumbnails.forEach((thumbnail, index) => {
      if (!(thumbnail instanceof HTMLElement)) return;
      if (matchingIndices.has(index)) {
        thumbnail.style.removeProperty('display');
        thumbnail.removeAttribute('hidden');
      } else {
        thumbnail.style.display = 'none';
        thumbnail.setAttribute('hidden', 'true');
      }
    });

    slides.forEach((slide, index) => {
      if (!(slide instanceof HTMLElement)) return;
      if (matchingIndices.has(index)) {
        slide.style.removeProperty('display');
        slide.removeAttribute('hidden');
      } else {
        slide.style.display = 'none';
        slide.setAttribute('hidden', 'true');
      }
    });

    const leadSlide = slides.find((slide, index) => matchingIndices.has(index));
    if (!(leadSlide instanceof HTMLElement)) return;

    slides.forEach((slide, index) => {
      if (!(slide instanceof HTMLElement) || !matchingIndices.has(index)) return;
      slide.setAttribute('aria-hidden', slide === leadSlide ? 'false' : 'true');
    });

    const slideId = leadSlide.getAttribute('slide-id');
    const slideshowComponent = mediaGallery.querySelector('slideshow-component');
    if (slideId && slideshowComponent) {
      slideshowComponent.select({ id: slideId }, undefined, { animate: false });
    }
  }

  /**
   * Resolves data-variant-ids for a slide index (slide attribute wins over thumbnail).
   * @param {HTMLElement[]} slides
   * @param {HTMLElement[]} thumbnails
   * @param {number} index
   */
  #getVariantIdsForIndex(slides, thumbnails, index) {
    const slide = slides[index];
    const thumb = thumbnails[index];
    const fromSlide = slide instanceof HTMLElement ? slide.getAttribute('data-variant-ids')?.trim() : '';
    if (fromSlide) return fromSlide;
    return thumb instanceof HTMLElement ? thumb.getAttribute('data-variant-ids')?.trim() ?? '' : '';
  }

  /**
   * Shows only slides/thumbnails for the selected variant's color in slideshow-controls.
   *
   * @param {HTMLElement} mediaGallery - The media gallery element to filter.
   * @param {string} variantId - Selected variant id.
   */
  async #filterMediaByVariantId(mediaGallery, variantId) {
    // Find the thumbnails container div (not the slideshow-controls element)
    const thumbnailsWrapper = mediaGallery.querySelector('.slideshow-controls__thumbnails');
    if (!thumbnailsWrapper || !(thumbnailsWrapper instanceof HTMLElement)) return;

    const thumbnails = Array.from(thumbnailsWrapper.querySelectorAll('.slideshow-controls__thumbnail'));
    if (thumbnails.length === 0) return;

    // Find all slides
    const slidesContainer = mediaGallery.querySelector('slideshow-slides');
    if (!slidesContainer || !(slidesContainer instanceof HTMLElement)) return;

    const slides = Array.from(slidesContainer.querySelectorAll('slideshow-slide'));
    if (slides.length === 0) return;

    const normalizedVariantId = String(variantId).trim();
    if (!normalizedVariantId) return;

    this.#prefilterMediaByVariantIdSync(mediaGallery, normalizedVariantId);

    let variantIdsToMatch = [normalizedVariantId];
    let matchingMediaIds = null;
    let featuredMediaId;

    const picker =
      mediaGallery.closest('.shopify-section')?.querySelector('variant-picker') ??
      document.querySelector('variant-picker[data-template-product-match="true"]');
    const productUrl = picker?.dataset?.productUrl;
    const VariantPickerClass = customElements.get('variant-picker')?.constructor;
    const getProductJson = VariantPickerClass?.getProductJson;

    if (productUrl && typeof getProductJson === 'function') {
      const product = await getProductJson(productUrl);
      if (product?.variants) {
        variantIdsToMatch = this.#getColorVariantIds(product, normalizedVariantId);
        matchingMediaIds = this.#getMediaIdsForSelectedColor(product, normalizedVariantId);
        featuredMediaId = this.#getFeaturedMediaId(product, normalizedVariantId);
      }
    }

    const count = Math.min(thumbnails.length, slides.length);
    const matchingIndices = new Set();

    for (let index = 0; index < count; index++) {
      const slide = slides[index];
      const thumb = thumbnails[index];
      const mediaId = this.#getMediaIdFromElement(slide) ?? this.#getMediaIdFromElement(thumb);

      if (matchingMediaIds) {
        if (mediaId && matchingMediaIds.has(mediaId)) {
          matchingIndices.add(index);
        } else if (!mediaId) {
          const ids = this.#getVariantIdsForIndex(slides, thumbnails, index);
          if (!ids || this.#variantIdsAttributeMatches(ids, variantIdsToMatch)) {
            matchingIndices.add(index);
          }
        }
        continue;
      }

      const ids = this.#getVariantIdsForIndex(slides, thumbnails, index);
      if (!ids) continue;
      if (this.#variantIdsAttributeMatches(ids, variantIdsToMatch)) {
        matchingIndices.add(index);
      }
    }

    // Only filter if we have at least one index to show; otherwise show all slides
    if (matchingIndices.size > 0) {
      // Filter to matching indices (slides/thumbs with empty data-variant-ids always match)
      thumbnails.forEach((thumbnail, index) => {
        if (!(thumbnail instanceof HTMLElement)) return;
        
        if (matchingIndices.has(index)) {
          thumbnail.style.removeProperty('display');
          thumbnail.removeAttribute('hidden');
        } else {
          thumbnail.style.display = 'none';
          thumbnail.setAttribute('hidden', 'true');
        }
      });
      
      slides.forEach((slide, index) => {
        if (!(slide instanceof HTMLElement)) return;
        
        if (matchingIndices.has(index)) {
          slide.style.removeProperty('display');
          slide.removeAttribute('hidden');
        } else {
          slide.style.display = 'none';
          slide.setAttribute('hidden', 'true');
        }
      });
    } else {
      // No matches found - show all slides (don't filter)
      thumbnails.forEach((thumbnail) => {
        if (thumbnail instanceof HTMLElement) {
          thumbnail.style.removeProperty('display');
          thumbnail.removeAttribute('hidden');
        }
      });
      slides.forEach((slide) => {
        if (slide instanceof HTMLElement) {
          slide.style.removeProperty('display');
          slide.removeAttribute('hidden');
        }
      });
      // Reset matchingIndices to include all for proper indexing
      thumbnails.forEach((_, index) => matchingIndices.add(index));
    }
    
    // Get only visible (matching) thumbnails and slides
    const visibleThumbnails = thumbnails.filter((thumb, index) => matchingIndices.has(index));
    const visibleSlides = slides.filter((slide, index) => matchingIndices.has(index));

    const featuredSlide =
      featuredMediaId != null
        ? visibleSlides.find((slide) => this.#slideMatchesFeaturedMedia(slide, featuredMediaId))
        : undefined;
    const leadSlide = featuredSlide ?? visibleSlides[0];

    visibleThumbnails.forEach((thumbnail, newIndex) => {
      if (!(thumbnail instanceof HTMLElement)) return;

      const domIndex = thumbnails.indexOf(thumbnail);
      const thumbSlide = domIndex >= 0 ? slides[domIndex] : null;
      const isFeatured =
        featuredMediaId != null &&
        thumbSlide instanceof HTMLElement &&
        this.#slideMatchesFeaturedMedia(thumbSlide, featuredMediaId);

      thumbnail.setAttribute('on:click', `/select/${newIndex}`);
      thumbnail.setAttribute('aria-label', `Slide ${newIndex + 1} of ${visibleThumbnails.length}`);
      thumbnail.classList.toggle('active', isFeatured || (!featuredMediaId && newIndex === 0));
      thumbnail.setAttribute('aria-selected', isFeatured || (!featuredMediaId && newIndex === 0) ? 'true' : 'false');
    });

    visibleSlides.forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      slide.removeAttribute('hidden');
      slide.style.removeProperty('display');
      slide.setAttribute('aria-hidden', slide === leadSlide ? 'false' : 'true');
    });

    const slideshowComponent = mediaGallery.querySelector('slideshow-component');

    if (visibleSlides.length > 0 && slideshowComponent) {
      if (featuredMediaId && featuredSlide) {
        slideshowComponent.select({ id: featuredMediaId }, undefined, { animate: false });
      } else if (leadSlide instanceof HTMLElement) {
        const slideId = leadSlide.getAttribute('slide-id');
        if (slideId) {
          slideshowComponent.select({ id: slideId }, undefined, { animate: false });
        } else {
          slideshowComponent.select(0, undefined, { animate: false });
        }
      }
    }

    if (mediaGallery === this && leadSlide instanceof HTMLElement) {
      const leadIndex = slides.indexOf(leadSlide);
      this.#updateThumbnailActiveStateBySlide(leadSlide, leadIndex >= 0 ? leadIndex : 0);
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles slideshow select events to update thumbnail active states.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    const { index, slide } = event.detail;
    // Use the slide element to find the corresponding thumbnail
    this.#updateThumbnailActiveStateBySlide(slide, index);
  };

  /**
   * Updates the active state of thumbnails based on the current slide element.
   * Matches thumbnails to slides by their index position.
   * @param {Element} currentSlide - The currently selected slide element.
   * @param {number} currentIndex - The current slide index from slideshow.
   */
  #updateThumbnailActiveStateBySlide(currentSlide, currentIndex) {
    const thumbnailsWrapper = this.querySelector('.slideshow-controls__thumbnails');
    if (!thumbnailsWrapper) return;

    const allThumbnails = Array.from(thumbnailsWrapper.querySelectorAll('.slideshow-controls__thumbnail'));
    const slidesContainer = this.querySelector('slideshow-slides');
    const allSlides = slidesContainer ? Array.from(slidesContainer.querySelectorAll('slideshow-slide')) : [];

    let slideIndex = currentIndex;
    if (currentSlide && allSlides.length > 0) {
      const foundIndex = allSlides.indexOf(currentSlide);
      if (foundIndex !== -1) {
        slideIndex = foundIndex;
      }
    }

    allThumbnails.forEach((thumbnail, thumbnailIndex) => {
      if (!(thumbnail instanceof HTMLElement)) return;

      if (thumbnail.style.display === 'none' || thumbnail.hasAttribute('hidden')) {
        thumbnail.classList.remove('active');
        thumbnail.setAttribute('aria-selected', 'false');
        return;
      }

      const isActive = thumbnailIndex === slideIndex;

      thumbnail.classList.toggle('active', isActive);
      thumbnail.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  /**
   * Updates the active state of thumbnails based on the current slide index.
   * Only updates visible (non-hidden) thumbnails.
   * @param {number} currentIndex - The current slide index from the slideshow (in the slideshow's slides array).
   */
  #updateThumbnailActiveState(currentIndex) {
    // Get the current slide from the slideshow
    if (this.slideshow?.slides && this.slideshow.slides[currentIndex]) {
      const currentSlide = this.slideshow.slides[currentIndex];
      this.#updateThumbnailActiveStateBySlide(currentSlide, currentIndex);
    } else {
      // Fallback: update by index only
      const thumbnailsWrapper = this.querySelector('.slideshow-controls__thumbnails');
      if (!thumbnailsWrapper) return;

      const allThumbnails = Array.from(thumbnailsWrapper.querySelectorAll('.slideshow-controls__thumbnail'));
      
      allThumbnails.forEach((thumbnail, thumbnailIndex) => {
        if (!(thumbnail instanceof HTMLElement)) return;
        
        // Skip hidden thumbnails
        if (thumbnail.style.display === 'none' || thumbnail.hasAttribute('hidden')) {
          thumbnail.classList.remove('active');
          thumbnail.setAttribute('aria-selected', 'false');
          return;
        }
        
        const isActive = thumbnailIndex === currentIndex;
        
        if (isActive) {
          thumbnail.classList.add('active');
          thumbnail.setAttribute('aria-selected', 'true');
        } else {
          thumbnail.classList.remove('active');
          thumbnail.setAttribute('aria-selected', 'false');
        }
      });
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
